import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { MsalGuard, MsalService } from '@azure/msal-angular';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';

import { NavigationTypeEnum } from '../enums/navigation-type.enum';
import { OrderTypeEnum } from '../enums/order-type.enum.model';
import { RolesEnum } from '../enums/roles.enum';
import { B2CPolicies } from '../models/auth-config.model';
import { Shop } from '../models/shop.model';
import { UserInfo, UserStatusEnumDTO } from '../models/user-info.model';
import { CoreService } from '../services/core.service';
import { UtilityFunctions } from '../utilities/functions.models';
import { AuthService } from './auth.service';
import { BackOfficeMenu } from 'app/back-office/shared/models/core/app-menus';
import { MAIN_LOADER_ID } from 'app/shop/shared/constants/shared.constants';
import { ContextService } from 'app/back-office/shared/services/core/context.service';

@Injectable({
  providedIn: 'root'
})
export class CanActivatAuthGuard implements CanActivate {

  constructor(
    private authService: AuthService,
    private msalService: MsalService,
    private msalGuard: MsalGuard,
    private _core: CoreService,
    private _router: Router,
    private _context: ContextService
  ) { }

  private activate(_auth: boolean, route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    let navigationType: NavigationTypeEnum = route.data['navigationType'];
    const allowedEmails = route.data['allowedEmails'] as string[];
    const currentUserEmail = this._context?.currentUserValue?.email;

    if (!navigationType) navigationType = NavigationTypeEnum.backOffice;
    if (allowedEmails && !allowedEmails.includes(currentUserEmail)) {
      UtilityFunctions.redirectToUnauthorized(this._router);
      return of(false);
    }
    if (_auth && !this.authService.tokenValue) {
      let allAccounts = this.msalService.instance.getAllAccounts();
      const account = allAccounts[0];
      return this.msalService.acquireTokenSilent({
        account: account,
        scopes: [B2CPolicies().scope],
        redirectUri: window.location.href
      }).pipe(
        catchError(() => {
          this.authService.logout(location.href);
          return of(null);
        }),
        switchMap((token: any) => {
          this.authService.setToken(token.accessToken);
          return this._core.getContext(MAIN_LOADER_ID)
            .pipe(map(data => {
              let result = false;
              const currentShop = UtilityFunctions.getCurrentShopByUrl(data.Stores);
              if (!currentShop) {
                const tenantStore = data.UserInfo.tenantStores.find(ts => !!UtilityFunctions.getCurrentShopByUrl(ts.stores))
                if (tenantStore) {
                  if (tenantStore.tenantId == data.UserInfo.tenant?.id) {
                    if (data.Stores?.length) location.href = data.Stores[0].uri;
                    else !!data.UserInfo.roles?.length ? this._router.navigateByUrl('welcome') : this._router.navigateByUrl('registration');
                  } else {
                    this._core.updateUserCurrentTenant(MAIN_LOADER_ID, tenantStore.tenantId).subscribe(() => window.location.reload())
                    return true;
                  }
                }
              } else {
                result = this.authorizeUserAndUrl(navigationType, route, data.UserInfo, currentShop);
                this.authService.setIsLogged(result);
              }
              return result;
            }));
        })
      );
    } else if (_auth) {
      const currentUser = this._core.userInfoChangedValue;
      const currentShop = this._core.currentShopValue;
      const ok = this.authorizeUserAndUrl(navigationType, route, currentUser, currentShop);
      this.authService.setIsLogged(ok);
      return of(ok);
    } else return of(!!_auth);
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    return this.msalGuard.canActivate(route, state)
      .pipe(concatMap((auth) => this.activate(auth as boolean, route)));
  }

  authorizeUserAndUrl(navigationType: NavigationTypeEnum, route: ActivatedRouteSnapshot, currentUser: UserInfo, currentShop: Shop): boolean {
    const rootRoles: RolesEnum[] = route.data['roles'];
    let checkUserResult: boolean = !!currentUser && this.checkUserRole(currentUser, rootRoles, navigationType);
    if (navigationType === NavigationTypeEnum.shop && checkUserResult) {
      if (currentUser.mainRole === RolesEnum.Designer) this.redirectToBackOffice(currentUser.mainRole);
      let mode: OrderTypeEnum = route.data['route'];
      if (mode === null || mode === undefined) mode = OrderTypeEnum.PreOrder;
      if (!!currentShop && !this.confirmValidShop(mode, currentShop, currentUser)) this.redirect(mode, currentShop, currentUser);
    }
    return checkUserResult;
  }

  checkUserRole(user: UserInfo, rootRoles: RolesEnum[], navigationType: NavigationTypeEnum): boolean {
    let userRoleResult: boolean = navigationType === NavigationTypeEnum.error;
    if (!user?.roles?.length) user.status = UserStatusEnumDTO.AwaitingAproval;
    if (!!user && !userRoleResult) {
      if (navigationType === NavigationTypeEnum.newUser && user.status !== UserStatusEnumDTO.AwaitingAproval) {
        UtilityFunctions.redirectToUnauthorized(this._router);
      }
      switch (user.status) {
        case UserStatusEnumDTO.Disabled:
          UtilityFunctions.redirectToUnauthorized(this._router);
          break;

        case UserStatusEnumDTO.AwaitingAproval:
          if (navigationType !== NavigationTypeEnum.newUser)
            !!user.roles?.length ? this._router.navigateByUrl('welcome') : this._router.navigateByUrl('registration');
          else
            userRoleResult = true;
          break;
        case UserStatusEnumDTO.Enabled:
          userRoleResult = !rootRoles?.length || rootRoles.some(r => user.roles.some(roleStr => RolesEnum[roleStr] === r));
          if (!userRoleResult) UtilityFunctions.redirectToUnauthorized(this._router);
          break;
      }
    }
    return !!userRoleResult;
  }

  private redirectToBackOffice(mainRole: RolesEnum) {
    const myUrl = UtilityFunctions.findFirstMenuItemByRole(BackOfficeMenu.items, mainRole);
    myUrl ? this._router.navigateByUrl('admin' + myUrl) : UtilityFunctions.redirectToUnauthorized(this._router);
  }

  private confirmValidShop(mode: OrderTypeEnum, shop: Shop, currentUser: UserInfo) {
    return shop && ((mode === OrderTypeEnum.PreOrder && UtilityFunctions.isPreOrderActive(shop, currentUser)) || (mode === OrderTypeEnum.ReOrder && UtilityFunctions.isReOrderActive(shop, currentUser)));
  }

  private redirect(mode: OrderTypeEnum, shop: Shop, currentUser: UserInfo) {
    const channelName = UtilityFunctions.returnChannelNameForRoot(this._core.currentActivatedChannelValue?.channel.name) || 'selectChannel';
    if (mode === OrderTypeEnum.PreOrder && this.confirmValidShop(OrderTypeEnum.ReOrder, shop, currentUser)) this._router.navigateByUrl('shop/' + channelName + '/re-order');
    else if (mode === OrderTypeEnum.ReOrder && this.confirmValidShop(OrderTypeEnum.PreOrder, shop, currentUser)) this._router.navigateByUrl('shop/' + channelName + '/pre-order');
    else this.redirectToBackOffice(currentUser.mainRole);
  }
}
